import { Injectable } from '@angular/core';
import * as ort from 'onnxruntime-web/webgpu';
import {
  DocPipeline,
  DocClassifier,
  DocRecognizer,
  loadClassifierMeta,
  type ClassifyDebug,
} from '../../lib/brdocs';
import {
  OnnxU2netp,
  YunetFaceDetector,
  TesseractOsd,
  OnnxClassifier,
  decodeImage,
  TESSERACT_CORE_PATH,
} from '../../lib/brdocs/browser';
import { DocPrediction } from './document-classify';

/** Model + sidecar served from the portal assets. */
const MODEL_URL = 'assets/brdocs/doc-cnn.onnx';
const META_URL = 'assets/brdocs/doc-cnn.json';
/** onnxruntime-web wasm/JSEP binaries are loaded from the matching CDN build. */
const ORT_VERSION = '1.27.0';

ort.env.wasm.wasmPaths = `https://cdn.jsdelivr.net/npm/onnxruntime-web@${ORT_VERSION}/dist/`;
ort.env.wasm.numThreads = 1; // avoid the cross-origin-isolation requirement
// Run the onnx sessions in ORT's proxy worker, OFF the main thread. Without this the
// first WebGPU inference compiles its shaders synchronously on the UI thread (~10s)
// and freezes the tab after the document upload — and a timeout can't help, because a
// synchronous run() can't be interrupted. Off-thread, the run no longer blocks the UI
// AND the existing guardCapture budget can actually abandon a slow one (→ keyword).
// Proxy is WASM-only (no WebGPU shader compile); a proxy failure degrades to keyword,
// never a freeze.
ort.env.wasm.proxy = true;

/** Re-exported for the docpic debug panel (shape lives in the brdocs lib). */
export type { ClassifyDebug };

/**
 * Thin Angular wrapper around the brdocs library's document recognizer. ALL the
 * preprocessing (localize → bgremove → deskew → orient) and classification (CNN
 * inference, OOD rejection, rotate-on-low-confidence) live in `src/lib/brdocs`
 * now — this service just wires the browser adapters, exposes signals, and maps
 * the lib's prediction onto the app's {@link DocPrediction}. The lib path is the
 * SAME one a Lambda would run, so the app and training stay in lock-step.
 *
 * If the model/sidecar aren't published or anything fails, {@link classify}
 * returns `null` so the caller falls back to the keyword classifier.
 */
@Injectable({ providedIn: 'root' })
export class CnnClassifier {
  private recognizer?: DocRecognizer;
  private loadPromise?: Promise<boolean>;
  /** True once the model is loaded and ready. */
  available = false;
  /** Which execution backend is active ('webgpu' | 'wasm' | ''). */
  backend = '';
  /** Inspection data from the most recent classify() — for the docpic debug panel. */
  lastDebug: ClassifyDebug | null = null;

  /** Lazily load the model + build the recognizer. Resolves false if absent. */
  load(): Promise<boolean> {
    this.loadPromise ??= (async () => {
      try {
        const meta = await loadClassifierMeta(META_URL);
        // WASM only: the sessions run in ORT's proxy worker (off the main thread),
        // and WebGPU isn't supported there — its on-thread shader compile was the
        // ~10s freeze. WASM in the worker keeps the UI responsive.
        const eps: ('webgpu' | 'wasm')[] = ['wasm'];
        const wasmPaths = ort.env.wasm.wasmPaths as string;

        const pipeline = new DocPipeline({
          segmentation: new OnnxU2netp({ modelUrl: 'assets/brdocs/doc-seg.onnx', executionProviders: eps, wasmPaths }),
          faceDetector: new YunetFaceDetector({ modelUrl: 'assets/brdocs/yunet.onnx', executionProviders: eps, wasmPaths }),
          osd: new TesseractOsd({ corePath: TESSERACT_CORE_PATH }),
        });
        const classifier = new DocClassifier(
          new OnnxClassifier({ modelUrl: MODEL_URL, executionProviders: eps, wasmPaths }),
          meta,
        );
        this.recognizer = new DocRecognizer({ pipeline, classifier });
        this.backend = 'wasm-proxy';
        this.available = true;
        return true;
      } catch (e) {
        console.warn('[CnnClassifier] load failed, falling back:', e);
        return false;
      }
    })();
    return this.loadPromise;
  }

  /** Classify a captured document image (data URL). Returns null if unavailable. */
  async classify(dataUrl: string): Promise<DocPrediction | null> {
    if (!(await this.load()) || !this.recognizer) return null;
    try {
      const pixels = await decodeImage(dataUrl);
      const result = await this.recognizer.recognize(pixels, { debug: true });
      // Surface what hit the model for the debug panel, but keep the ORIGINAL
      // capture as the strip's "raw" (the lib uses the classify input there).
      if (result.debug) this.lastDebug = { ...result.debug, raw: dataUrl };

      const p = result.prediction;
      return {
        type: p.type,
        label: p.label,
        confidence: p.confidence,
        source: 'model',
        experts: p.experts.map((e) => ({ modality: this.backend, type: e.type, prob: e.prob })),
      };
    } catch (e) {
      console.warn('[CnnClassifier] inference failed:', e);
      return null;
    }
  }
}
